/// <reference types="signalr" />

import { useEffect, useRef, useState, useCallback } from "react";
import $ from "jquery";

interface IncomingMessage {
  text: string;
  sender: string;
  timestamp: string;
  mediaId?: string;
  mediaType?: string;
  mediaUrl?: string;
  fileName?: string;
}

let globalConnection: SignalR.Hub.Connection | null = null;
let globalProxy: SignalR.Hub.Proxy | null = null;
let globalPhone: string | null = null;
let globalMessageListener: ((msg: IncomingMessage) => void) | null = null;
let isModuleDisposed = false;

// const isStatusPayloadText = (text: string) => {
//   const normalized = String(text || "").trim();
//   if (!normalized) return true;
//   return (
//     (normalized.includes('"object":"whatsapp_business_account"') &&
//       normalized.includes('"statuses"')) ||
//     (normalized.includes('"messaging_product":"whatsapp"') &&
//       normalized.includes('"recipient_id"') &&
//       normalized.includes('"pricing"'))
//   );
// };

export function useSignalR(phone: string | null) {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<IncomingMessage[]>([]);
  const lastMessageKeyRef = useRef<string>("");

  const signalRBaseUrl = window.location.href.includes(import.meta.env.VITE_API_TARGET_URL)
    ? `${import.meta.env.VITE_API_TARGET_URL}api`
    : `${import.meta.env.VITE_API_BASE_URL}api`;
  const signalRHubUrl = window.location.href.includes(import.meta.env.VITE_API_TARGET_URL)
    ? `${import.meta.env.VITE_API_TARGET_URL}api/signalr`
    : `${import.meta.env.VITE_API_BASE_URL}api/signalr`;

  const joinGroup = (proxy: SignalR.Hub.Proxy, phoneNumber: string) => {
    const room = phoneNumber.replace(/\D/g, ""); // strip non-digits — matches server logic
    if (!room) return;
    proxy.invoke("JoinGroup", room)
      .fail((err: any) => {
        console.error("JoinGroup failed:", err);
      });
  };

  const startConnection = useCallback(async (phoneNumber: string) => {
    await loadSignalR();
    const connection = $.hubConnection(signalRBaseUrl, {
      useDefaultPath: false,
    });

    connection.url = signalRHubUrl;

    const token = localStorage.getItem("access_token") ?? "";
    if (token) {
      connection.qs = {
        access_token: token,
        token,
        Authorization: token,
      };
    }

    const proxy = connection.createHubProxy("notificationHub");

    const emitMessage = (
      text: string,
      direction: string,
      incomingPhone?: string,
      mediaId?: string | null,
      mediaType?: string | null,
      mediaUrl?: string | null,
      fileName?: string | null
    ) => {
      const normalizedText = String(text || "").trim();

      const key = `${incomingPhone}|${normalizedText}|${direction}|${mediaId || ""}`;
      if (lastMessageKeyRef.current === key) return;
      lastMessageKeyRef.current = key;

      const msg: any = {
        text: normalizedText,
        sender: String(direction).toLowerCase() === "outgoing" ? "me" : "them",
        timestamp: new Date().toISOString(),
        mediaId: mediaId || undefined,
        mediaType: mediaType || undefined,
        mediaUrl: mediaUrl || undefined,
        fileName: fileName || undefined,
      };
      globalMessageListener?.(msg);
    };

    const onReceive = (
      fromPhoneOrPayload: any,
      content?: string,
      plugin?: string,
      direction?: string
    ) => {
      const payload =
        fromPhoneOrPayload && typeof fromPhoneOrPayload === "object"
          ? fromPhoneOrPayload
          : null;

      const candidatePhone =
        payload?.toPhone ??
        payload?.to ??
        payload?.recipient ??
        payload?.phone ??
        payload?.toNumber ??
        payload?.fromPhone ??
        payload?.from ??
        (typeof fromPhoneOrPayload === "string" ? fromPhoneOrPayload : "");

      const messageText =
        payload?.content ?? payload?.text ?? payload?.message ??
        payload?.MessageText ?? payload?.Message ?? content ?? "";
      const messageDirection = payload?.direction ?? payload?.Direction ?? direction ?? "incoming";
      const mediaId = payload?.mediaId ?? payload?.MediaId ?? null;
      const mediaType = payload?.mediaType ?? payload?.MediaType ?? "text";
      const mediaUrl = payload?.mediaUrl ?? payload?.MediaUrl ?? null;
      const fileName = payload?.fileName ?? payload?.FileName ?? null;

      emitMessage(messageText, messageDirection, candidatePhone, mediaId, mediaType, mediaUrl, fileName);
    };

    proxy.on("receiveMessage", onReceive);
    proxy.on("ReceiveMessage", onReceive);
    proxy.on("messageReceived", onReceive);
    proxy.on("MessageReceived", onReceive);


    connection.reconnecting(() => {
      setIsConnected(false);
    });

    connection.reconnected(() => {
      setIsConnected(true);
      joinGroup(proxy, phoneNumber);
    });

    connection.disconnected(() => {
      setIsConnected(false);
      setTimeout(() => {
        if (isModuleDisposed) return;
        if (globalConnection === connection) {
          connection
            .start({ transport: ["webSockets", "serverSentEvents", "longPolling"] })
            .done(() => {
              setIsConnected(true);
              joinGroup(proxy, phoneNumber);
            });
        }
      }, 8000);
    });

    connection
      .start({ transport: ["webSockets", "serverSentEvents", "longPolling"] })
      .done(() => {
        setIsConnected(true);
        globalPhone = phoneNumber;
        joinGroup(proxy, phoneNumber);
      })
      .fail((err: any) => {
        setIsConnected(false);
        console.error("SignalR connection failed:", err);
      });

    globalConnection = connection;
    globalProxy = proxy;
  }, [signalRBaseUrl, signalRHubUrl]);

  useEffect(() => {
    if (!phone) return;

    const listener = (msg: IncomingMessage) => {
      setMessages((prev) => [...prev, msg]);
    };
    globalMessageListener = listener;

    if (
      globalConnection &&
      globalPhone === phone &&
      globalConnection.state === $.signalR.connectionState.connected
    ) {
      setIsConnected(true);
      return;
    }

    if (globalConnection) {
      const oldProxy = globalProxy;
      const oldPhone = globalPhone;

      if (
        oldProxy &&
        globalConnection.state === $.signalR.connectionState.connected &&
        oldPhone
      ) {
        const oldRoom = oldPhone.replace(/\D/g, "");
        oldProxy.invoke("LeaveGroup", oldRoom).always(() => {
          globalConnection?.stop();
          globalConnection = null;
          globalProxy = null;
          globalPhone = null;
          void startConnection(phone);
        });
      } else {
        globalConnection.stop();
        globalConnection = null;
        globalProxy = null;
        globalPhone = null;
        void startConnection(phone);
      }
    } else {
      void startConnection(phone);
    }

    return () => {
      if (globalMessageListener === listener) {
        globalMessageListener = null;
      }
    };
  }, [phone, startConnection]);

  useEffect(() => {
    if (!phone) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (
        globalConnection &&
        globalProxy &&
        globalPhone === phone &&
        globalConnection.state === $.signalR.connectionState.connected
      ) {
        joinGroup(globalProxy, phone);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [phone]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return { isConnected, messages, clearMessages };
}

if ((import.meta as any).hot) {
  (import.meta as any).hot.dispose(() => {
    isModuleDisposed = true;
    if (globalConnection) {
      globalConnection.stop();
      globalConnection = null;
      globalProxy = null;
      globalPhone = null;
    }
  });
}